<script setup lang="ts">
import type { Band } from '~/types/ui/uiTypes'

/**
 * A page section: a kicker and a meta label, a heading, a subtitle, and actions to the right.
 * `badge` sits above the title, `details` under the subtitle, and the default slot below
 * everything. Level 1 opens a page, 2 is a section of it and 3 a part of a section. It sits on a
 * band wash; band="none" draws the heading block alone, for one that is a column of its parent.
 */
const props = withDefaults(
  defineProps<{
    title: string
    subtitle?: string
    level?: 1 | 2 | 3
    band?: Band | 'none'
    as?: 'article' | 'aside' | 'div' | 'section'
    /** Carbon icon class beside the title. */
    icon?: string
    kicker?: string
    kickerIcon?: string
    meta?: string | null
    metaIcon?: string
  }>(),
  {
    subtitle: undefined,
    level: 1,
    band: 'member',
    as: 'div',
    icon: undefined,
    kicker: undefined,
    kickerIcon: undefined,
    meta: undefined,
    metaIcon: undefined,
  },
)

defineSlots<{
  badge?: () => unknown
  details?: () => unknown
  actions?: () => unknown
  default?: () => unknown
}>()

const LEVELS = {
  1: { size: 'xl', head: 'mb-3', subtitle: 'max-w-2xl text-[1.02rem]/relaxed', body: 'mt-6' },
  2: { size: 'lg', head: 'mb-2', subtitle: 'max-w-sm text-[0.95rem]/relaxed', body: 'mt-6' },
  3: { size: 'md', head: 'mb-2', subtitle: 'text-sm/relaxed', body: 'mt-3' },
} as const

const look = computed(() => LEVELS[props.level])

const UICard = resolveComponent('UICard')
const surface = computed(() => props.band === 'none' ? props.as : UICard)
const surfaceProps = computed(() => props.band === 'none' ? {} : { variant: 'band', band: props.band, as: props.as })
</script>

<template>
  <!-- A div, not a header: app.vue's critical CSS paints every header with the nav bar's dark background. -->
  <component :is="surface" v-bind="surfaceProps" class="ui-section">
    <div class="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
      <div class="min-w-0">
        <div v-if="kicker || meta" class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1" :class="look.head">
          <UIText v-if="kicker" variant="kicker" class="inline-flex items-center gap-1.5">
            <span v-if="kickerIcon" :class="kickerIcon" class="size-3.5" aria-hidden="true" />
            {{ kicker }}
          </UIText>
          <UIText v-if="meta" variant="label" class="inline-flex items-center gap-1.5">
            <span v-if="metaIcon" :class="metaIcon" class="size-3.5" aria-hidden="true" />
            {{ meta }}
          </UIText>
        </div>

        <div v-if="$slots.badge" class="mb-3">
          <slot name="badge" />
        </div>

        <UIHeading :level="level" :size="look.size" class="flex items-center gap-3">
          <span v-if="icon" :class="icon" class="size-8 shrink-0 text-accent-2" aria-hidden="true" />
          {{ title }}
        </UIHeading>

        <p v-if="subtitle" class="mt-3 text-subtitle" :class="look.subtitle">
          {{ subtitle }}
        </p>

        <div v-if="$slots.details" class="mt-3">
          <slot name="details" />
        </div>
      </div>

      <div v-if="$slots.actions" class="relative z-10 flex shrink-0 items-center gap-2">
        <slot name="actions" />
      </div>
    </div>

    <div v-if="$slots.default" :class="look.body">
      <slot />
    </div>
  </component>
</template>
